import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './FreeSpinLauncher.css';

export default function FreeSpinLauncher({ visible = true, label = 'Free Spin', isLoggedIn = false }) {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);
  const [showMsg, setShowMsg] = useState(false);
  const [msgText, setMsgText] = useState('Please login or sign up to use Free Spin and make a deposit.');
  const [checking, setChecking] = useState(false);

  const checkEligibility = async () => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      const remoteId = typeof window !== 'undefined' ? localStorage.getItem('remote_id') : null;
      const API = process.env.REACT_APP_API_URL;
      if (!token || !remoteId || !API) return false;
      const res = await fetch(`${API}/api/transaction/show?remote_id=${encodeURIComponent(remoteId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return false;
      const txJson = await res.json();
      const txs = Array.isArray(txJson?.transactions) ? txJson.transactions : (txJson?.data || txJson || []);

      const now = new Date();
      const threeDaysAgo = new Date(now);
      threeDaysAgo.setDate(now.getDate() - 3);
      threeDaysAgo.setHours(0, 0, 0, 0);
      const todayStr = now.toDateString();

      const isSuccessStatus = (t) => {
        const keyVal = t?.key;
        if (keyVal === 1 || keyVal === '1') return true;
        const st = String(t?.status ?? '').toLowerCase();
        return ['1', 'success', 'completed', 'approved', 'ok', 'done'].includes(st);
      };
      const parseDate = (t) => {
        const raw = t?.created_at || t?.createdAt || t?.date || t?.time;
        if (!raw) return null;
        if (/^\d+$/.test(String(raw))) {
          const num = Number(raw);
          const ms = num > 1e12 ? num : (num < 1e12 ? num * 1000 : num);
          const dNum = new Date(ms);
          return Number.isNaN(dNum.getTime()) ? null : dNum;
        }
        const dotFmt = /^\s*(\d{1,2})[\.\/-](\d{1,2})[\.\/-](\d{2,4})\s*$/;
        const mDot = String(raw).match(dotFmt);
        if (mDot) {
          let [_, dd, mm, yy] = mDot;
          const year = yy.length === 2 ? (Number(yy) + 2000) : Number(yy);
          const month = Number(mm) - 1;
          const day = Number(dd);
          const d = new Date(year, month, day);
          return Number.isNaN(d.getTime()) ? null : d;
        }
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? null : d;
      };

      let hasRecentDeposit = false;
      let hasFreeSpinToday = false;

      for (const t of txs) {
        const actionRaw = String(t?.action || t?.type || '');
        const normalized = actionRaw.toLowerCase().replace(/[^a-z]/g, '');
        const createdAt = parseDate(t);
        if (!createdAt) continue;
        if (!hasRecentDeposit && normalized.includes('deposit') && isSuccessStatus(t) && createdAt >= threeDaysAgo) {
          hasRecentDeposit = true;
        }
        if (!hasFreeSpinToday && normalized === 'freespin' && createdAt.toDateString() === todayStr) {
          hasFreeSpinToday = true;
        }
        if (hasRecentDeposit && hasFreeSpinToday) break;
      }

      return { hasRecentDeposit, hasFreeSpinToday };
    } catch (e) {
      return false;
    }
  };

  if (!visible || hidden) return null;

  return (
    <>
      <button
        aria-label="Open Free Spin"
        className="fs-float-launcher"
        onClick={async () => {
          if (!isLoggedIn) {
            setMsgText('Please login or sign up to use Free Spin and make a deposit.');
            setShowMsg(true);
            return;
          }
          setChecking(true);
          const result = await checkEligibility();
          setChecking(false);
          if (result && typeof result === 'object') {
            if (result.hasFreeSpinToday) {
              setMsgText('You have already claimed your free spin for today!');
              setShowMsg(true);
              return;
            }
            if (result.hasRecentDeposit) {
              navigate('/free-spin');
              return;
            }
          }
          if (result === true) {
            navigate('/free-spin');
          } else {
            setMsgText('A deposit in the last 3 days is required to use Free Spin.');
            setShowMsg(true);
          }
        }}
      >
        <img className="fs-float-logo" src="/poster/freespin.gif" alt="Free Spin" />
        <span
          className="fs-float-close"
          role="button"
          aria-label="Close launcher"
          onClick={(e) => { e.stopPropagation(); setHidden(true); }}
        >
          ×
        </span>
      </button>

      {showMsg && (
        <div className="fs-login-msg" role="dialog" aria-live="polite">
          <div className="fs-login-msg-inner">
            <div className="fs-login-msg-title">Free Spin</div>
            <div className="fs-login-msg-body">{msgText}</div>
            <div className="fs-login-actions">
              {!isLoggedIn && (
                <>
                  <button className="fs-login-btn" onClick={() => navigate('/login')}>Login</button>
                  <button className="fs-signup-btn" onClick={() => navigate('/signup')}>Sign Up</button>
                </>
              )}
              <button className="fs-msg-close" onClick={() => setShowMsg(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
